import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Video, Calendar as CalendarIcon, TrendingUp, Target, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const ScoreGauge = ({ score }) => {
  const getScoreColor = (s) => {
    if (s >= 90) return 'text-green-400';
    if (s >= 80) return 'text-emerald-400';
    if (s >= 70) return 'text-yellow-400';
    if (s >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreLabel = (s) => {
    if (s >= 90) return 'Excelente';
    if (s >= 80) return 'Muy Bueno';
    if (s >= 70) return 'Bueno';
    if (s >= 60) return 'Regular';
    return 'Necesita Trabajo';
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-lg">
      <div className={`text-5xl font-bold ${getScoreColor(score)}`}>
        {score || '--'}
      </div>
      <div className="text-sm text-muted-foreground mt-1">Puntuación General</div>
      <div className={`text-xs font-semibold mt-1 ${getScoreColor(score)}`}>
        {score ? getScoreLabel(score) : 'Sin evaluar'}
      </div>
    </div>
  );
};

const PhaseCard = ({ phase }) => {
  const getPhaseColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-semibold text-sm">{phase.fase}</h5>
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getPhaseColor(phase.puntuacion)} text-white`}>
          {phase.puntuacion}
        </span>
      </div>
      <Progress value={phase.puntuacion} className="h-2 mb-3" />
      <p className="text-xs text-muted-foreground mb-2">{phase.analisis}</p>
      {phase.puntos_clave && phase.puntos_clave.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {phase.puntos_clave.map((punto, idx) => (
            <span key={idx} className="text-xs bg-white/10 px-2 py-0.5 rounded">
              {punto}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const TempoAnalysis = ({ tempo }) => {
  if (!tempo) return null;

  const getTempoColor = (evaluacion) => {
    if (evaluacion?.toLowerCase().includes('bueno') || evaluacion?.toLowerCase().includes('excelente')) return 'text-green-400';
    if (evaluacion?.toLowerCase().includes('regular')) return 'text-yellow-400';
    return 'text-orange-400';
  };

  return (
    <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg border border-purple-500/20">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-5 w-5 text-purple-400" />
        <h4 className="font-semibold">Análisis de Tempo</h4>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Evaluación</p>
          <p className={`font-semibold ${getTempoColor(tempo.evaluacion)}`}>{tempo.evaluacion || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Ratio</p>
          <p className="font-semibold">{tempo.ratio || 'N/A'}</p>
        </div>
      </div>
      {tempo.observaciones && (
        <p className="text-sm text-muted-foreground mt-3">{tempo.observaciones}</p>
      )}
      {tempo.recomendacion && (
        <div className="mt-3 p-2 bg-white/5 rounded">
          <p className="text-xs text-purple-300">💡 {tempo.recomendacion}</p>
        </div>
      )}
    </div>
  );
};

export const Vault = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});

  useEffect(() => {
    const fetchAnalyses = async () => {
      try {
        const response = await api.get('/analysis');
        setAnalyses(response.data);
      } catch (error) {
        toast.error('Error al cargar análisis');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyses();
  }, []);

  const togglePhases = (analysisId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [analysisId]: !prev[analysisId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="vault-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12 lg:px-24" data-testid="vault-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2" data-testid="vault-title">Informes de Entrenamiento</h1>
          <p className="text-muted-foreground" data-testid="vault-subtitle">Tu historial completo de análisis y progreso</p>
        </div>

        {analyses.length === 0 ? (
          <Card className="glass-effect border-white/10" data-testid="empty-vault">
            <CardContent className="py-16 text-center">
              <Video className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No tenés análisis aún</h3>
              <p className="text-muted-foreground mb-6">Subí tu primer video para comenzar</p>
              <Link 
                to="/analysis" 
                className="inline-block bg-primary text-primary-foreground px-6 py-3 font-bold uppercase tracking-wider hover:bg-primary/90 transition-all"
                data-testid="upload-first-video-link"
              >
                Subir Video
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {analyses.map((analysis) => (
              <Card key={analysis.id} className="glass-effect border-white/10 hover:border-primary/50 transition-colors" data-testid={`analysis-card-${analysis.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="font-heading uppercase flex items-center gap-2">
                        <Video className="h-5 w-5 text-accent" />
                        {analysis.video_filename}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-2">
                        <CalendarIcon className="h-4 w-4" />
                        {new Date(analysis.created_at).toLocaleDateString('es-AR', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {analysis.score && (
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${
                            analysis.score >= 80 ? 'text-green-400' : 
                            analysis.score >= 70 ? 'text-yellow-400' : 'text-orange-400'
                          }`}>
                            {analysis.score}
                          </div>
                          <div className="text-xs text-muted-foreground">puntos</div>
                        </div>
                      )}
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        analysis.processing_status === 'completed' 
                          ? 'bg-primary/20 text-primary' 
                          : analysis.processing_status === 'error'
                          ? 'bg-red-500/20 text-red-500'
                          : 'bg-yellow-500/20 text-yellow-500'
                      }`} data-testid={`status-${analysis.id}`}>
                        {analysis.processing_status === 'completed' ? 'Completado' : 
                         analysis.processing_status === 'error' ? 'Error' : 'Procesando...'}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {analysis.processing_status === 'completed' && (
                  <CardContent>
                    {/* Score y Tempo Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <ScoreGauge score={analysis.score} />
                      <TempoAnalysis tempo={analysis.tempo_analysis} />
                    </div>

                    {/* Fases del Swing */}
                    {analysis.swing_phases && analysis.swing_phases.length > 0 && (
                      <div className="mb-6">
                        <button 
                          onClick={() => togglePhases(analysis.id)}
                          className="flex items-center gap-2 w-full p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                        >
                          <Target className="h-5 w-5 text-accent" />
                          <span className="font-semibold">Análisis por Fase del Swing</span>
                          <span className="ml-auto">
                            {expandedPhases[analysis.id] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </span>
                        </button>
                        {expandedPhases[analysis.id] && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                            {analysis.swing_phases.map((phase, idx) => (
                              <PhaseCard key={idx} phase={phase} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <Tabs defaultValue="summary" className="w-full">
                      <TabsList className="grid w-full grid-cols-3 bg-white/5">
                        <TabsTrigger value="summary" data-testid={`tab-summary-${analysis.id}`}>Resumen</TabsTrigger>
                        <TabsTrigger value="strengths" data-testid={`tab-strengths-${analysis.id}`}>Fortalezas</TabsTrigger>
                        <TabsTrigger value="weaknesses" data-testid={`tab-weaknesses-${analysis.id}`}>Debilidades</TabsTrigger>
                      </TabsList>

                      <TabsContent value="summary" className="mt-4" data-testid={`content-summary-${analysis.id}`}>
                        <div className="p-4 bg-white/5 rounded-lg">
                          <p className="text-muted-foreground">{analysis.summary || 'Procesando análisis...'}</p>
                        </div>
                      </TabsContent>

                      <TabsContent value="strengths" className="mt-4" data-testid={`content-strengths-${analysis.id}`}>
                        <div className="space-y-3">
                          {analysis.strengths?.map((strength, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                              <span className="text-primary font-bold text-lg mt-1">✓</span>
                              <div>
                                <p className="font-semibold">{strength.aspecto || strength.aspect}</p>
                                <p className="text-sm text-muted-foreground">{strength.descripcion || strength.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      <TabsContent value="weaknesses" className="mt-4" data-testid={`content-weaknesses-${analysis.id}`}>
                        <div className="space-y-3">
                          {analysis.weaknesses?.map((weakness, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                              <span className="text-yellow-500 font-bold text-lg mt-1">⚠</span>
                              <div>
                                <p className="font-semibold">{weakness.aspecto || weakness.aspect}</p>
                                <p className="text-sm text-muted-foreground">{weakness.descripcion || weakness.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>

                    {analysis.drills && analysis.drills.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-accent" />
                          Ejercicios Recomendados
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {analysis.drills.map((drill, idx) => (
                            <div key={idx} className="p-4 bg-accent/10 border border-accent/30 rounded-lg" data-testid={`drill-${analysis.id}-${idx}`}>
                              <p className="font-semibold text-sm">{drill.nombre || drill.name}</p>
                              <p className="text-xs text-muted-foreground mt-1">{drill.descripcion || drill.description}</p>
                              <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-accent">{drill.duracion || drill.duration}</span>
                                {drill.objetivo && (
                                  <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{drill.objetivo}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
